import * as THREE from 'three';

/**
 * Arrowhead sci-fi interceptor — brick/pixel build.
 * Twin-EDF arrowhead jet: wide delta planform, grey/black only (no glow accents),
 * extra triangle wedges for faceted jet surfaces.
 * Local +Z is nose-forward (same as player craft).
 */
const GREY = 0x9aa0a8;
const GREY_MID = 0x6a717c;
const GREY_DARK = 0x3e444e;
const BLACK = 0x14171c;
const BLACK_SOFT = 0x252a32;
const PANEL = 0x4a515c;
const INTAKE = 0x0c0e12;
const CANOPY = 0x080a0e;
/** Dim “jet” — industrial white/grey, not a neon thruster. */
const JET_DIM = 0xc5c9d0;
const JET_CORE = 0xe8eaee;
const EDGE = 0xb0b6be;

type Mats = {
  grey: THREE.MeshBasicMaterial;
  greyMid: THREE.MeshBasicMaterial;
  greyDark: THREE.MeshBasicMaterial;
  black: THREE.MeshBasicMaterial;
  blackSoft: THREE.MeshBasicMaterial;
  panel: THREE.MeshBasicMaterial;
  intake: THREE.MeshBasicMaterial;
  canopy: THREE.MeshBasicMaterial;
  jetDim: THREE.MeshBasicMaterial;
  jetCore: THREE.MeshBasicMaterial;
  edge: THREE.MeshBasicMaterial;
};

function mats(): Mats {
  return {
    grey: new THREE.MeshBasicMaterial({ color: GREY }),
    greyMid: new THREE.MeshBasicMaterial({ color: GREY_MID }),
    greyDark: new THREE.MeshBasicMaterial({ color: GREY_DARK }),
    black: new THREE.MeshBasicMaterial({ color: BLACK }),
    blackSoft: new THREE.MeshBasicMaterial({ color: BLACK_SOFT }),
    panel: new THREE.MeshBasicMaterial({ color: PANEL }),
    intake: new THREE.MeshBasicMaterial({ color: INTAKE }),
    canopy: new THREE.MeshBasicMaterial({ color: CANOPY }),
    jetDim: new THREE.MeshBasicMaterial({ color: JET_DIM }),
    jetCore: new THREE.MeshBasicMaterial({ color: JET_CORE }),
    edge: new THREE.MeshBasicMaterial({ color: EDGE }),
  };
}

function brick(
  parent: THREE.Object3D,
  w: number,
  h: number,
  d: number,
  mat: THREE.Material,
  x: number,
  y: number,
  z: number,
  rotY = 0,
  rotZ = 0,
  rotX = 0,
) {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  mesh.position.set(x, y, z);
  mesh.rotation.set(rotX, rotY, rotZ);
  parent.add(mesh);
  return mesh;
}

/** Low-poly triangular wedge (3-sided cone) — jet planform accents. */
function triWedge(
  parent: THREE.Object3D,
  radius: number,
  length: number,
  mat: THREE.Material,
  x: number,
  y: number,
  z: number,
  rotX = 0,
  rotY = 0,
  rotZ = 0,
) {
  const mesh = new THREE.Mesh(new THREE.ConeGeometry(radius, length, 3), mat);
  mesh.rotation.x = Math.PI / 2;
  mesh.position.set(x, y, z);
  mesh.rotateX(rotX);
  mesh.rotateY(rotY);
  mesh.rotateZ(rotZ);
  parent.add(mesh);
  return mesh;
}

export function createEnemyShip(): THREE.Group {
  const ship = new THREE.Group();
  ship.name = 'arcade-enemy';
  const m = mats();

  // ——— Central spine / arrowhead fuselage ———
  brick(ship, 0.82, 0.4, 2.35, m.greyMid, 0, 0.04, 0.05);
  brick(ship, 0.64, 0.34, 0.68, m.grey, 0, 0.04, 1.3);
  brick(ship, 0.44, 0.26, 0.48, m.panel, 0, 0.02, 1.85);
  brick(ship, 0.26, 0.18, 0.36, m.blackSoft, 0, 0.0, 2.25);
  // Stacked nose wedges = sharp triangular point
  triWedge(ship, 0.22, 0.48, m.black, 0, 0.0, 2.58);
  triWedge(ship, 0.14, 0.28, m.blackSoft, 0, 0.02, 2.42, 0.08, 0, 0);

  // Chin / ventral intake (black arrowhead under-nose)
  brick(ship, 0.48, 0.15, 0.8, m.intake, 0, -0.25, 1.3);
  brick(ship, 0.3, 0.11, 0.38, m.black, 0, -0.28, 1.85);
  triWedge(ship, 0.15, 0.32, m.black, 0, -0.26, 2.18, 0.18, 0, 0);
  triWedge(ship, 0.12, 0.25, m.intake, 0, -0.22, 1.55, 0.25, Math.PI, 0);

  // Dorsal ridge + facet wedges
  brick(ship, 0.15, 0.13, 1.85, m.edge, 0, 0.3, 0.1);
  brick(ship, 0.11, 0.09, 0.42, m.blackSoft, 0, 0.32, 1.25);
  triWedge(ship, 0.12, 0.35, m.panel, 0, 0.36, 0.9, -0.4, 0, 0);
  triWedge(ship, 0.1, 0.28, m.blackSoft, 0, 0.34, 0.2, -0.35, 0, 0);

  // Flush black canopy
  brick(ship, 0.4, 0.2, 0.7, m.canopy, 0, 0.36, 0.5);
  brick(ship, 0.26, 0.1, 0.32, m.black, 0, 0.46, 0.7);
  triWedge(ship, 0.14, 0.3, m.canopy, 0, 0.4, 0.95, -0.2, 0, 0);

  // ——— Wide delta / arrowhead wing ———
  // Broad rear base → stepped forward triangle
  brick(ship, 3.55, 0.09, 1.15, m.grey, 0, -0.04, -0.4);
  brick(ship, 2.85, 0.085, 0.55, m.greyMid, 0, -0.02, 0.32);
  brick(ship, 2.0, 0.08, 0.48, m.panel, 0, 0.0, 0.82);
  brick(ship, 1.15, 0.075, 0.42, m.greyDark, 0, 0.02, 1.22);
  brick(ship, 0.55, 0.07, 0.32, m.blackSoft, 0, 0.03, 1.52);

  // Leading-edge chevrons (angled plates)
  brick(ship, 1.25, 0.07, 0.68, m.greyMid, 1.25, -0.02, -0.08, 0, 0, 0.34);
  brick(ship, 1.25, 0.07, 0.68, m.greyMid, -1.25, -0.02, -0.08, 0, 0, -0.34);
  brick(ship, 0.95, 0.065, 0.52, m.blackSoft, 1.7, 0.0, 0.32, 0, 0, 0.5);
  brick(ship, 0.95, 0.065, 0.52, m.blackSoft, -1.7, 0.0, 0.32, 0, 0, -0.5);
  // Outer tip points
  brick(ship, 0.6, 0.06, 0.42, m.black, 2.05, 0.02, 0.55, 0, 0, 0.58);
  brick(ship, 0.6, 0.06, 0.42, m.black, -2.05, 0.02, 0.55, 0, 0, -0.58);
  triWedge(ship, 0.2, 0.38, m.black, 2.15, 0.02, 0.72, 0, 0.15, 0.6);
  triWedge(ship, 0.2, 0.38, m.black, -2.15, 0.02, 0.72, 0, -0.15, -0.6);

  // Trailing-edge notches
  brick(ship, 1.05, 0.065, 0.34, m.greyDark, 1.05, -0.06, -0.9);
  brick(ship, 1.05, 0.065, 0.34, m.greyDark, -1.05, -0.06, -0.9);
  brick(ship, 0.45, 0.06, 0.26, m.black, 1.55, -0.05, -0.72);
  brick(ship, 0.45, 0.06, 0.26, m.black, -1.55, -0.05, -0.72);

  // Wing-root + surface triangle facets (extra wedges)
  triWedge(ship, 0.42, 0.82, m.greyDark, 0.65, -0.02, 0.8, 0, 0.12, 0.38);
  triWedge(ship, 0.42, 0.82, m.greyDark, -0.65, -0.02, 0.8, 0, -0.12, -0.38);
  triWedge(ship, 0.28, 0.55, m.panel, 1.0, 0.02, 0.35, 0, 0.18, 0.45);
  triWedge(ship, 0.28, 0.55, m.panel, -1.0, 0.02, 0.35, 0, -0.18, -0.45);
  triWedge(ship, 0.22, 0.42, m.blackSoft, 1.35, 0.04, 0.05, 0, 0.22, 0.5);
  triWedge(ship, 0.22, 0.42, m.blackSoft, -1.35, 0.04, 0.05, 0, -0.22, -0.5);
  // Underside delta facets
  triWedge(ship, 0.3, 0.5, m.blackSoft, 0.75, -0.1, 0.15, 0.15, 0.1, 0.3);
  triWedge(ship, 0.3, 0.5, m.blackSoft, -0.75, -0.1, 0.15, 0.15, -0.1, -0.3);
  // Mid-wing “sawtooth” triangles
  triWedge(ship, 0.18, 0.32, m.greyDark, 1.6, 0.0, -0.25, 0, 0.3, 0.35);
  triWedge(ship, 0.18, 0.32, m.greyDark, -1.6, 0.0, -0.25, 0, -0.3, -0.35);

  // ——— Twin rear EDF pods (dim white/grey exhaust only) ———
  const jetBody = new THREE.CylinderGeometry(0.26, 0.3, 1.05, 6);
  const jetRing = new THREE.CylinderGeometry(0.28, 0.28, 0.1, 6);
  for (const sx of [1, -1] as const) {
    const x = sx * 0.58;
    const pod = new THREE.Mesh(jetBody, m.greyMid);
    pod.rotation.x = Math.PI / 2;
    pod.position.set(x, -0.1, -1.4);
    ship.add(pod);

    const lip = new THREE.Mesh(jetRing, m.black);
    lip.rotation.x = Math.PI / 2;
    lip.position.set(x, -0.1, -0.9);
    ship.add(lip);

    const nozzle = new THREE.Mesh(jetRing, m.blackSoft);
    nozzle.rotation.x = Math.PI / 2;
    nozzle.position.set(x, -0.1, -1.9);
    ship.add(nozzle);

    // Dim industrial exhaust (no cyan/orange glow)
    brick(ship, 0.28, 0.28, 0.14, m.jetDim, x, -0.1, -2.02);
    brick(ship, 0.18, 0.18, 0.1, m.jetCore, x, -0.1, -2.14);

    // Fairing wedges pod → wing
    triWedge(ship, 0.2, 0.45, m.panel, x * 0.92, -0.02, -0.72, 0, sx * 0.28, sx * 0.22);
    triWedge(ship, 0.14, 0.3, m.blackSoft, x * 0.85, 0.04, -1.05, 0, sx * 0.2, sx * 0.15);
  }

  // Belly keel
  brick(ship, 0.3, 0.13, 1.45, m.black, 0, -0.34, -0.2);
  triWedge(ship, 0.12, 0.35, m.blackSoft, 0, -0.36, 0.55, 0.2, 0, 0);

  // Vertical stabilizer — tall swept triangle fin
  brick(ship, 0.09, 0.62, 0.68, m.greyDark, 0, 0.55, -0.88);
  brick(ship, 0.07, 0.4, 0.4, m.blackSoft, 0, 0.78, -0.68);
  triWedge(ship, 0.2, 0.48, m.grey, 0, 0.72, -0.48, -0.38, 0, 0);
  triWedge(ship, 0.12, 0.3, m.panel, 0, 0.88, -0.55, -0.45, 0, 0);
  brick(ship, 0.06, 0.14, 0.16, m.black, 0, 0.98, -0.58);

  // Small twin ventral fins (black triangle accents near tail)
  triWedge(ship, 0.1, 0.28, m.black, 0.22, -0.28, -1.15, 0.5, 0.1, 0.15);
  triWedge(ship, 0.1, 0.28, m.black, -0.22, -0.28, -1.15, 0.5, -0.1, -0.15);

  ship.userData.radius = 2.6;
  return ship;
}
